import { getLast7DaysDateRange } from "../pages/Rajasthan/Utility/const";
import { generatePairedDateRanges } from "../pages/Rajasthan/Demand/Data";
import {
  generateDateRangesForYearRange,
  generateMonthsFrom2021ToLatest,
} from "../pages/Gridco/Demand/Data";

const MONTH_NAMES = [
  "JAN",
  "FEB",
  "MAR",
  "APR",
  "MAY",
  "JUN",
  "JUL",
  "AUG",
  "SEP",
  "OCT",
  "NOV",
  "DEC",
];

function pad2(n) {
  return String(n).padStart(2, "0");
}

function formatDate(d) {
  return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
}

function monthRangeValue(year, month) {
  const firstDay = `${year}-${pad2(month + 1)}-01`;
  const lastDay = `${year}-${pad2(month + 1)}-${pad2(new Date(year, month + 1, 0).getDate())}`;
  return `${firstDay},${lastDay}`;
}

function monthRangeLabel(year, month) {
  return `${MONTH_NAMES[month]}-${String(year).slice(-2)}`;
}

function getCurrentMonthRange() {
  const now = new Date();
  return monthRangeValue(now.getFullYear(), now.getMonth());
}

function getCurrentMonthLabel() {
  const now = new Date();
  return monthRangeLabel(now.getFullYear(), now.getMonth());
}

function getYesterday() {
  const d = new Date();
  d.setDate(d.getDate() - 1);
  return d;
}

function getDaysBack(n) {
  const d = new Date();
  d.setDate(d.getDate() - n);
  return d;
}

const getCurrentDatePlus6Months = () => {
  const now = new Date();
  const future = new Date(now.getFullYear(), now.getMonth() + 5, now.getDate());
  return { month: future.getMonth(), year: future.getFullYear() };
};

const MONTHS_FROM_2021 = generateMonthsFrom2021ToLatest();

const currentLabel = getCurrentMonthLabel();
const currentRange = getCurrentMonthRange();

// ─── Shared / Cross-tenant ──────────────────────────────────────────

export const ConsumptionDefaultDates = {
  Overview: {
    type: "range",
    defaultRangeDate: (() => {
      return {
        startDate: getDaysBack(6),
        endDate: getDaysBack(2),
        key: "selection",
      };
    })(),
  },
  "Trend Analysis": {
    type: "trend",
    trendDateOptions: MONTHS_FROM_2021,
    defaultTrendDates: [MONTHS_FROM_2021[0]],
  },
  Demand: {
    type: "range",
    defaultRangeDate: getLast7DaysDateRange(),
  },
  Generation: {
    type: "range",
    defaultRangeDate: getLast7DaysDateRange(),
  },
  Outage: {
    type: "range",
    defaultRangeDate: getLast7DaysDateRange(),
  },
};

export const ExchangeAnalysisDefaultDates = {
  Overview: {
    type: "range",
    defaultRangeDate: getLast7DaysDateRange(),
  },
  "Trend Analysis": {
    type: "trend",
    trendDateOptions: MONTHS_FROM_2021,
    defaultTrendDates: [MONTHS_FROM_2021[0]],
  },
  Realtime: {
    type: "range",
    defaultRangeDate: [],
    dateRangeDateOptions: [],
    showDateRange: true,
  },
  "Buyers & Sellers": {
    type: "range",
    defaultRangeDate: getLast7DaysDateRange(),
  },
  "Entity Analysis": {
    type: "range",
    defaultRangeDate: getLast7DaysDateRange(),
  },
};

export const ExchangeAnalysisHPPCDefaultDates = {
  Collective: {
    type: "range",
    defaultRangeDate: getLast7DaysDateRange(),
  },
  "Trend Analysis": {
    type: "trend",
    trendDateOptions: MONTHS_FROM_2021,
    defaultTrendDates: [MONTHS_FROM_2021[0]],
  },
  Realtime: {
    type: "range",
    defaultRangeDate: [],
    dateRangeDateOptions: [],
    showDateRange: true,
  },
  "Buyers & Sellers": {
    type: "range",
    defaultRangeDate: getLast7DaysDateRange(),
  },
  "Entity Analysis": {
    type: "range",
    defaultRangeDate: getLast7DaysDateRange(),
  },
  Derivative: {
    type: "trend",
    trendDateOptions: MONTHS_FROM_2021,
    defaultTrendDates: [MONTHS_FROM_2021[0]],
  },
  "Power Market": {
    type: "range",
    defaultRangeDate: {
      startDate: getYesterday(),
      endDate: new Date(),
    },
    trendDateOptions: (() => {
      const options = [];
      const startYear = 2024;
      const startMonth = 0;
      const now = new Date();
      const endYear = now.getFullYear();
      const endMonth = now.getMonth();
      for (let year = endYear; year >= startYear; year--) {
        const mStart = year === endYear ? endMonth : 11;
        const mEnd = year === startYear ? startMonth : 0;
        for (let month = mStart; month >= mEnd; month--) {
          options.push({
            value: monthRangeValue(year, month),
            label: monthRangeLabel(year, month),
          });
        }
      }
      return options;
    })(),
    defaultTrendDates: (() => {
      const d4 = getDaysBack(4);
      const d3 = getDaysBack(3);
      return [
        {
          value: `${formatDate(d4)},${formatDate(d4)}`,
          label: `${pad2(d4.getDate())}-${MONTH_NAMES[d4.getMonth()]}-${String(d4.getFullYear()).slice(-2)}`,
        },
        {
          value: `${formatDate(d3)},${formatDate(d3)}`,
          label: `${pad2(d3.getDate())}-${MONTH_NAMES[d3.getMonth()]}-${String(d3.getFullYear()).slice(-2)}`,
        },
      ];
    })(),
  },
  "Vidyut AI": {
    type: "range",
    defaultRangeDate: getLast7DaysDateRange(),
  },
};

export const PriceForecastDefaultDates = {
  DAM: {
    type: "range",
    defaultRangeDate: (() => {
      const today = new Date();
      const startDate = new Date(today);
      startDate.setDate(today.getDate() - 15);
      return {
        startDate: startDate,
        endDate: today,
        key: "selection",
      };
    })(),
  },
  RTM: {
    type: "range",
    defaultRangeDate: getLast7DaysDateRange(),
  },
};

// ─── Rajasthan / RUVNL ──────────────────────────────────────────────

export const RuvitalDemand = {
  "Discom Load ": {
    type: "range",
    // defaultRangeDate: getLast7DaysDateRange(),
    defaultRangeDate: {
      startDate: new Date("2026-01-05"),
      endDate: new Date("2026-01-11"),
      key: "selection",
    },
  },
  "Trend Analysis": {
    type: "trend",
    trendDateOptions: generatePairedDateRanges(
      { month: 0, year: 2021 }, // Actual start: Jan 2021
      { month: new Date().getMonth(), year: new Date().getFullYear() }, // Actual end: current month/year
      { month: 8, year: 2025 }, // Forecast start: Jan 2025
      getCurrentDatePlus6Months(), // Forecast end: current date + 6 months
      "Model 1",
    ),
    defaultTrendDates: [
      { value: "2026-01-01,2026-01-31", label: "JAN-26" },
      {
        value: "2026-01-01,2026-01-31,Model 1",
        label: "JAN-26 (Forecast)",
        forecast: true,
        model: "Model 1",
      },
    ],
  },
  // "Trend Analysis": {
  //   type: "trend",

  //   trendDateOptions: generatePairedDateRanges(
  //     { month: 0, year: 2021 },
  //     { month: new Date().getMonth(), year: new Date().getFullYear() },
  //     { month: 8, year: 2025 },
  //     getCurrentDatePlus6Months(),
  //     "Model 1",
  //   ),

  //   defaultTrendDates: [
  //     {
  //       value: currentRange,
  //       label: currentLabel,
  //     },
  //     {
  //       value: `${currentRange},Model 1`,
  //       label: `${currentLabel} (Forecast)`,
  //       forecast: true,
  //       model: "Model 1",
  //     },
  //   ],
  // },
  "Restricted Demand": {
    type: "range",
    // defaultRangeDate: getLast7DaysDateRange(),
    defaultRangeDate: {
      startDate: new Date("2026-01-05"),
      endDate: new Date("2026-01-11"),
      key: "selection",
    },
  },
  "Restricted Demand Trend Analysis": {
    type: "trend",
    trendDateOptions: generatePairedDateRanges(
      { month: 0, year: 2021 }, // Actual start: Jan 2021
      { month: new Date().getMonth(), year: new Date().getFullYear() }, // Actual end: current month/year
      undefined, // Forecast start: undefined
      undefined, // Forecast end: undefined
      "Model 1",
    ),
    defaultTrendDates: [
      // { value: "2025-11-01,2025-11-30,Model 1", label: "NOV-25 Forecast" },
      { value: "2026-01-01,2026-01-31", label: "JAN-26" },
      // { value: `${currentRange}`, label: `${currentLabel}` },
    ],
  },
  "Surplus/Deficit Analysis": {
    type: "range",
    defaultRangeDate: getLast7DaysDateRange(),
  },
};

export const RuvitalOverview = {
  "By Summary": {
    type: "range",
    // defaultRangeDate: getLast7DaysDateRange(),
    defaultRangeDate: {
      startDate: new Date("2026-01-05"),
      endDate: new Date("2026-01-11"),
      key: "selection",
    },
  },

  "By Product": {
    type: "range",
    // defaultRangeDate: getLast7DaysDateRange(),
    defaultRangeDate: {
      startDate: new Date("2026-01-05"),
      endDate: new Date("2026-01-11"),
      key: "selection",
    },
  },
};

export const RuvitalPowerProcurement = {
  Overview: {
    type: "range",
    defaultRangeDate: {
      startDate: new Date("2026-01-05"),
      endDate: new Date("2026-01-11"),
      key: "selection",
    },
    // defaultRangeDate: getLast7DaysDateRange(),
  },
  Collective: {
    type: "range",
    defaultRangeDate: {
      startDate: new Date("2026-01-05"),
      endDate: new Date("2026-01-11"),
      key: "selection",
    },
    // defaultRangeDate: getLast7DaysDateRange(),
  },
  "GNA/TGNA": {
    type: "range",
    defaultRangeDate: {
      startDate: new Date("2026-01-05"),
      endDate: new Date("2026-01-11"),
      key: "selection",
    },
    // defaultRangeDate: getLast7DaysDateRange(),
  },
  "Trend Analysis": {
    type: "range",
    // trendDateOptions: DEFAULT_DATES_GENRATION_TREND,
    trendDateOptions: generatePairedDateRanges(
      { month: 0, year: 2023 },
      { month: new Date().getMonth(), year: new Date().getFullYear() },
    ),
    defaultTrendDates: [
      { value: currentRange, label: currentLabel },
      // {
      //   value: currentRange,
      //   label: currentLabel,
      // },
    ],
  },
};

export const RuvitalGenration = {
  Overview: {
    type: "range",
    // defaultRangeDate: getLast7DaysDateRange(),
    defaultRangeDate: {
      startDate: new Date("2026-01-05"),
      endDate: new Date("2026-01-11"),
      key: "selection",
    },
  },
  "Trend Analysis": {
    type: "trend",
    trendDateOptions: generatePairedDateRanges(
      { month: 0, year: 2023 },
      { month: new Date().getMonth(), year: new Date().getFullYear() },
    ),
    // defaultTrendDates: [{ value: `${currentRange}`, label: `${currentLabel}` }],
    // trendDateOptions: DEFAULT_DATES_GENRATION_TREND,
    defaultTrendDates: [{ value: currentRange, label: currentLabel }],
  },
  "By Source": {
    type: "range",
    defaultRangeDate: {
      startDate: new Date("2026-01-05"),
      endDate: new Date("2026-01-11"),
      key: "selection",
    },
    // defaultRangeDate: getLast7DaysDateRange(),
  },
  "By Power Station": {
    type: "range",
    defaultRangeDate: {
      startDate: new Date("2026-01-05"),
      endDate: new Date("2026-01-11"),
      key: "selection",
    },
    // defaultRangeDate: getLast7DaysDateRange(),
    dateRangeDateOptions: [],
    showDateRange: false,
  },
  "Operating Parameters": {
    type: "range",
    defaultRangeDate: getLast7DaysDateRange(),
  },
  Outage: {
    type: "range",
    defaultRangeDate: getLast7DaysDateRange(),
  },
};

// Last 7 days ending T-1 (yesterday) — JVVNL metrics freshness is T-1.
function getJvvnlLast7Days() {
  const end = new Date();
  end.setHours(0, 0, 0, 0);
  end.setDate(end.getDate() - 1);
  const start = new Date(end);
  start.setDate(end.getDate() - 6);
  return { startDate: start, endDate: end, key: "selection" };
}

export const JvvnlOverview = {
  Overview: {
    type: "range",
    defaultRangeDate: {
      startDate: new Date("2026-05-23"),
      endDate: new Date("2026-05-28"),
      key: "selection",
    },
  },
};
export const JvvnlOverviewMetric = {
  Metric: {
    type: "range",
    defaultRangeDate: getJvvnlLast7Days(),
  },
};

export const JvvnlGeneration = {
  "By Plant": {
    type: "range",
    defaultRangeDate: {
      startDate: new Date("2026-05-23"),
      endDate: new Date("2026-05-28"),
      key: "selection",
    },
  },
  "Trend Analysis": {
    type: "trend",
    trendDateOptions: generatePairedDateRanges(
      { month: 0, year: 2026 },
      { month: new Date().getMonth(), year: new Date().getFullYear() },
    ),
    defaultTrendDates: [{ value: "2026-05-01,2026-05-31", label: "May-26" }],
  },
};

// ─── Gridco ─────────────────────────────────────────────────────────

export const GridcoPowerProcurementDefaultDates = {
  Overview: {
    type: "range",
    defaultRangeDate: {
      startDate: new Date("2026-02-22"),
      endDate: new Date("2026-02-28"),
      key: "selection",
    },
  },
  "Trend Analysis": {
    type: "trend",
    trendDateOptions: generatePairedDateRanges(
      { month: 0, year: 2022 },
      { month: new Date().getMonth(), year: new Date().getFullYear() },
    ),
    defaultTrendDates: [{ value: "2026-02-01,2026-02-28", label: "FEB-26" }],
  },
  Collective: {
    type: "range",
    defaultRangeDate: {
      startDate: new Date("2026-02-22"),
      endDate: new Date("2026-02-28"),
      key: "selection",
    },
  },
  "GNA/TGNA": {
    type: "range",
    defaultRangeDate: {
      startDate: new Date("2026-02-22"),
      endDate: new Date("2026-02-28"),
      key: "selection",
    },
  },
};

export const GridcoPowerGenrationDefaultDates = {
  Overview: {
    type: "range",
    defaultRangeDate: {
      startDate: new Date("2026-02-22"),
      endDate: new Date("2026-02-28"),
      key: "selection",
    },
  },

  "Trend Analysis": {
    type: "trend",
    trendDateOptions: generatePairedDateRanges(
      { month: 0, year: 2022 },
      { month: new Date().getMonth(), year: new Date().getFullYear() },
    ),
    defaultTrendDates: [{ value: "2026-02-01,2026-02-28", label: "FEB-26" }],
  },
  ISGS: {
    type: "range",
    defaultRangeDate: {
      startDate: new Date("2026-03-25"),
      endDate: new Date("2026-03-31"),
      key: "selection",
    },
  },
  Intrastate: {
    type: "range",
    defaultRangeDate: {
      startDate: new Date("2026-03-25"),
      endDate: new Date("2026-03-31"),
      key: "selection",
    },
  },
};

export const GridcoPowerDemandDefaultDates = {
  "Discom Load": {
    type: "range",
    defaultRangeDate: {
      startDate: new Date("2026-02-22"),
      endDate: new Date("2026-02-28"),
      key: "selection",
    },
  },
  "Trend Analysis": {
    type: "trend",
    trendDateOptions: generateDateRangesForYearRange(2022, 2026, "JAN", "FEB"),
    defaultTrendDates: [{ value: "2026-02-01,2026-02-28", label: "FEB-26" }],
  },
  "Sub Station with OA": {
    type: "range",
    defaultRangeDate: {
      startDate: new Date("2026-02-22"),
      endDate: new Date("2026-02-28"),
      key: "selection",
    },
  },
  "Sub Station with OA Trend Analysis": {
    type: "trend",
    trendDateOptions: generateDateRangesForYearRange(2022, 2025),
    defaultTrendDates: [{ value: "2026-02-01,2026-02-28", label: "FEB-26" }],
  },
};

export const GridcoOverviewDefaultDates = {
  "By Summary": {
    type: "range",
    defaultRangeDate: {
      startDate: new Date("2026-02-22"),
      endDate: new Date("2026-02-28"),
      key: "selection",
    },
  },

  "By Product": {
    type: "range",
    defaultRangeDate: {
      startDate: new Date("2026-02-22"),
      endDate: new Date("2026-02-28"),
      key: "selection",
    },
  },
};

// ─── Haryana / HPPC ─────────────────────────────────────────────────

export const HaryanaPowerProcurement = {
  // APPC: {
  //   type: "range",
  //   defaultRangeDate: {
  //     startDate: (() => {
  //       const yesterday = new Date();
  //       yesterday.setDate(yesterday.getDate() - 1);
  //       return yesterday;
  //     })(),
  //     endDate: (() => {
  //       const yesterday = new Date();
  //       yesterday.setDate(yesterday.getDate() - 1);
  //       return yesterday;
  //     })(),
  //     key: "selection",
  //   },
  // },

  "GNA/TGNA": {
    type: "range",
    defaultRangeDate: {
      startDate: getYesterday(),
      endDate: getYesterday(),
      key: "selection",
    },
  },
};

export const HpgcOverviewDefaultDates = {
  Overview: {
    type: "range",
    defaultRangeDate: {
      startDate: new Date("2025-02-09"),
      endDate: new Date("2025-02-10"),
      key: "selection",
    },
  },
};

export const HpgcDemandDefaultDates = {
  "Haryana Load": {
    type: "trend",
    trendDateOptions: (() => {
      const options = [];
      const startYear = 2015;
      const currentDate = new Date();
      const endYear = currentDate.getFullYear();
      const endMonth = currentDate.getMonth();
      for (let year = endYear; year >= startYear; year--) {
        const monthStart = year === endYear ? endMonth : 11;
        const monthEnd = year === startYear ? 0 : 0;
        for (let month = monthStart; month >= monthEnd; month--) {
          options.push({
            value: monthRangeValue(year, month),
            label: monthRangeLabel(year, month),
          });
        }
      }
      return options;
    })(),
    defaultTrendDates: (() => {
      const y = getYesterday();
      return [
        {
          value: `${formatDate(y)},${formatDate(y)}`,
          label: `${pad2(y.getDate())}-${MONTH_NAMES[y.getMonth()]}-${String(y.getFullYear()).slice(-2)}`,
        },
      ];
    })(),
  },

  "Peak Analysis": {
    type: "trend",
    trendDateOptions: generatePairedDateRanges(
      { month: 0, year: 2015 },
      { month: new Date().getMonth(), year: new Date().getFullYear() },
      { month: 0, year: 2025 },
      { month: 8, year: 2026 },
      "Model 1",
    ),
    defaultTrendDates: (() => {
      const y = getYesterday();
      return [
        {
          value: `${formatDate(y)},${formatDate(y)}`,
          label: `${pad2(y.getDate())}-${MONTH_NAMES[y.getMonth()]}-${String(y.getFullYear()).slice(-2)}`,
        },
      ];
    })(),
  },

  "Trend Analysis": {
    type: "trend",
    trendDateOptions: generatePairedDateRanges(
      { month: 0, year: 2015 }, // Actual start: Jan 2021
      { month: new Date().getMonth(), year: new Date().getFullYear() }, // Actual end: current month/year
      { month: 0, year: 2025 }, // Forecast start: Jan 2025
      { month: 8, year: 2026 }, // Forecast end: Apr 2026
      "Model 1",
    ),
    defaultTrendDates: (() => {
      const now = new Date();
      const yr = now.getFullYear();
      const mo = now.getMonth();
      const val = monthRangeValue(yr, mo);
      const lbl = monthRangeLabel(yr, mo);
      return [
        { value: val, label: lbl },
        {
          value: `${val}, Model 1`,
          label: `${lbl} (Forecast)`,
          forecast: true,
          model: "Model 1",
        },
      ];
    })(),
  },

  "Surplus/Deficit Analysis": {
    type: "trend",
    trendDateOptions: (() => {
      const options = [];
      const startYear = 2025;
      const startMonth = 10;
      const endYear = 2026;
      const endMonth = 11;
      for (let year = startYear; year <= endYear; year++) {
        const mStart = year === startYear ? startMonth : 0;
        const mEnd = year === endYear ? endMonth : 11;
        for (let month = mStart; month <= mEnd; month++) {
          options.push({
            value: monthRangeValue(year, month),
            label: monthRangeLabel(year, month),
          });
        }
      }
      return options;
    })(),
    defaultTrendDates: (() => {
      const today = new Date();
      const tomorrow = new Date(today);
      tomorrow.setDate(tomorrow.getDate() + 1);
      return [
        {
          value: `${formatDate(today)},${formatDate(today)}`,
          label: `${pad2(today.getDate())}-${MONTH_NAMES[today.getMonth()]}-${String(today.getFullYear()).slice(-2)}`,
        },
        {
          value: `${formatDate(tomorrow)},${formatDate(tomorrow)}`,
          label: `${pad2(tomorrow.getDate())}-${MONTH_NAMES[tomorrow.getMonth()]}-${String(tomorrow.getFullYear()).slice(-2)}`,
        },
      ];
    })(),
  },
};

// ─── Chhattisgarh ───────────────────────────────────────────────────

export const ChhattisgarhDefaultDates = {
  "Discom Load ": {
    type: "range",
    defaultRangeDate: getLast7DaysDateRange(),
    // defaultRangeDate: {
    //   startDate: new Date("2025-12-01"),
    //   endDate: new Date("2025-12-07"),
    //   key: "selection",
    // },
  },
  Demand: {
    type: "range",
    defaultRangeDate: (() => {
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      return {
        startDate: today,
        endDate: today,
        key: "selection",
      };
    })(),
  },
  Generation: {
    type: "range",
    defaultRangeDate: (() => {
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      return {
        startDate: today,
        endDate: today,
        key: "selection",
      };
    })(),
  },
  Summary: {
    type: "range",
    defaultRangeDate: (() => {
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      return {
        startDate: today,
        endDate: today,
        key: "selection",
      };
    })(),
  },
  "Trend Analysis": {
    type: "trend",
    trendDateOptions: generatePairedDateRanges(
      { month: 0, year: 2021 }, // Actual start: Jan 2021
      { month: new Date().getMonth(), year: new Date().getFullYear() }, // Actual end: current month/year
      { month: 8, year: 2025 }, // Forecast start: Jan 2025
      getCurrentDatePlus6Months(), // Forecast end: current date + 6 months
      "Model 1",
    ),
    defaultTrendDates: [
      { value: currentRange, label: currentLabel },
      {
        value: `${currentRange},Model 1`,
        label: `${currentLabel} (Forecast)`,
        forecast: true,
        model: "Model 1",
      },
    ],
  },
  "Forecast Discom Load": {
    type: "range",
    defaultRangeDate: getLast7DaysDateRange(),
  },
  "DC vs Actual": {
    type: "range",
    defaultRangeDate: getLast7DaysDateRange(),
  },
  "Generation Profile": {
    type: "range",
    defaultRangeDate: getLast7DaysDateRange(),
  },
};

export const ChhattisgarhGenerationDefaultDates = {
  Overview: {
    type: "range",
    defaultRangeDate: getLast7DaysDateRange(),
    // defaultRangeDate: {
    //   startDate: new Date("2026-01-05"),
    //   endDate: new Date("2026-01-11"),
    //   key: "selection",
    // },
  },
  "Trend Analysis": {
    type: "trend",
    trendDateOptions: generatePairedDateRanges(
      { month: 0, year: 2023 },
      { month: new Date().getMonth(), year: new Date().getFullYear() },
    ),
    // defaultTrendDates: [{ value: `${currentRange}`, label: `${currentLabel}` }],
    // trendDateOptions: DEFAULT_DATES_GENRATION_TREND,
    defaultTrendDates: [{ value: currentRange, label: currentLabel }],
  },
  "By Source": {
    type: "range",
    defaultRangeDate: {
      startDate: new Date("2026-01-05"),
      endDate: new Date("2026-01-11"),
      key: "selection",
    },
    // defaultRangeDate: getLast7DaysDateRange(),
  },
  "By Power Station": {
    type: "range",
    defaultRangeDate: {
      startDate: new Date("2026-01-05"),
      endDate: new Date("2026-01-11"),
      key: "selection",
    },
    // defaultRangeDate: getLast7DaysDateRange(),
    dateRangeDateOptions: [],
    showDateRange: false,
  },
  "Operating Parameters": {
    type: "range",
    defaultRangeDate: getLast7DaysDateRange(),
  },
  Outage: {
    type: "range",
    defaultRangeDate: getLast7DaysDateRange(),
  },
};

// ─── Power Analytics ────────────────────────────────────────────────

export const PowerAnalyticsDefaultDates = {
  "Power Market": {
    type: "range",
    defaultRangeDate: {
      startDate: getYesterday(),
      endDate: new Date(),
    },
    trendDateOptions: (() => {
      const options = [];
      const startYear = 2024;
      const startMonth = 0;
      const now = new Date();
      const endYear = now.getFullYear();
      const endMonth = now.getMonth();
      for (let year = endYear; year >= startYear; year--) {
        const mStart = year === endYear ? endMonth : 11;
        const mEnd = year === startYear ? startMonth : 0;
        for (let month = mStart; month >= mEnd; month--) {
          options.push({
            value: monthRangeValue(year, month),
            label: monthRangeLabel(year, month),
          });
        }
      }
      return options;
    })(),
    defaultTrendDates: (() => {
      const d4 = getDaysBack(4);
      const d3 = getDaysBack(3);
      return [
        {
          value: `${formatDate(d4)},${formatDate(d4)}`,
          label: `${pad2(d4.getDate())}-${MONTH_NAMES[d4.getMonth()]}-${String(d4.getFullYear()).slice(-2)}`,
        },
        {
          value: `${formatDate(d3)},${formatDate(d3)}`,
          label: `${pad2(d3.getDate())}-${MONTH_NAMES[d3.getMonth()]}-${String(d3.getFullYear()).slice(-2)}`,
        },
      ];
    })(),
  },
};
